/**
 * Sistema de Logging Unificado
 * 
 * 🚀 PRODUCTION SAFETY: Em produção, erros são enviados ao Sentry
 * 📝 DEVELOPMENT: Logs no console para debugging
 * 
 * Este é o único logger que deve ser usado no codebase.
 * NÃO use console.log diretamente, use logger.* em vez disso.
 */

import * as Sentry from '@sentry/react';

type LoggableValue = string | number | boolean | null | undefined | Error | object;

const isDev = import.meta.env.DEV;
const isSentryEnabled = !!import.meta.env.VITE_SENTRY_DSN;

/**
 * Sensitive fields that should be redacted before sending to external services
 */
const SENSITIVE_PATTERNS = [
  /password/i,
  /senha/i,
  /token/i,
  /secret/i,
  /api[_-]?key/i,
  /authorization/i,
  /bearer/i,
  /cpf/i,
  /cnpj/i,
  /credit[_-]?card/i,
  /card[_-]?number/i,
  /cvv/i,
  /ssn/i,
];

/**
 * Sanitizes an object by redacting sensitive fields
 */
function sanitizeData(data: unknown, depth = 0): unknown {
  // Prevent infinite recursion
  if (depth > 5) return '[MAX_DEPTH]';
  
  if (data === null || data === undefined) return data;
  
  if (typeof data === 'string') {
    // Redact potential tokens/keys in strings
    if (data.length > 20 && /^(ey|sk_|pk_|key_)/i.test(data)) {
      return '[REDACTED_TOKEN]';
    }
    return data;
  }
  
  if (typeof data !== 'object') return data;
  
  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item, depth + 1));
  }
  
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    // Check if key matches sensitive patterns
    const isSensitive = SENSITIVE_PATTERNS.some(pattern => pattern.test(key));
    
    if (isSensitive) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeData(value, depth + 1);
    } else {
      sanitized[key] = sanitizeData(value, depth + 1);
    }
  }
  return sanitized;
}

/**
 * Converte args para um objeto de contexto para o Sentry (sanitizado)
 */
function argsToExtra(args: LoggableValue[]): Record<string, unknown> | undefined {
  if (args.length === 0) return undefined;
  
  const sanitizedArgs = args.map(arg => sanitizeData(arg));
  
  if (sanitizedArgs.length === 1 && typeof sanitizedArgs[0] === 'object' && sanitizedArgs[0] !== null && !(args[0] instanceof Error)) {
    return sanitizedArgs[0] as Record<string, unknown>;
  }
  return { details: sanitizedArgs };
}

/**
 * Extrai a mensagem principal dos argumentos
 */
function extractMessage(args: LoggableValue[]): string {
  if (args.length === 0) return '';
  const first = args[0];
  if (typeof first === 'string') return first;
  if (first instanceof Error) return first.message;
  return String(first);
}

export const logger = {
  /**
   * Log informativo - apenas desenvolvimento
   */
  info: (...args: LoggableValue[]) => {
    if (isDev) {
      console.info(...args);
    }
    // Info logs não são enviados ao Sentry para economizar quota
  },
  
  /**
   * Warning - vira breadcrumb (não issue) + console em dev.
   * Use `error` quando algo realmente falhou.
   */
  warn: (...args: LoggableValue[]) => {
    if (isDev) {
      console.warn(...args);
    }
    if (isSentryEnabled) {
      Sentry.addBreadcrumb({
        category: 'logger',
        level: 'warning',
        message: extractMessage(args),
        data: argsToExtra(args.slice(1)),
        timestamp: Date.now() / 1000,
      });
    }
  },
  
  /**
   * Error - sempre logado, enviado ao Sentry em produção
   */
  error: (...args: LoggableValue[]) => {
    if (isDev) {
      console.error(...args);
    }
    
    if (isSentryEnabled) {
      const firstArg = args[0];
      
      // Se for um Error, capturar como exceção
      if (firstArg instanceof Error) {
        Sentry.captureException(firstArg, {
          extra: argsToExtra(args.slice(1)),
        });
      } else {
        // Caso contrário, capturar como mensagem de erro
        Sentry.captureMessage(extractMessage(args), {
          level: 'error',
          extra: argsToExtra(args.slice(1)),
        });
      }
    }
  },
  
  /**
   * Debug - apenas desenvolvimento, nunca em produção
   */
  debug: (...args: LoggableValue[]) => {
    if (isDev) {
      console.log('[DEBUG]', ...args);
    }
  },

  /**
   * Trace - logging mais granular, apenas desenvolvimento
   */
  trace: (...args: LoggableValue[]) => {
    if (isDev) {
      console.log('[TRACE]', ...args);
    }
  },
};
